'use client'

import { useState, useEffect, useMemo } from "react"
import { motion, AnimatePresence } from "framer-motion"
import { Lock, ShoppingCart, Star, Award } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Label } from "@/components/ui/label"
import { useCurrentUser } from "@/hooks/useCurrentUser"
import { AccessResult } from "@/components/access/types"
import { CheckoutFormBlock } from '@/components/funnel/blocks/CheckoutFormBlock'
import { buildPurchaseContext } from "@/lib/checkout/buildPurchaseContext"

type GateStep = 'preview' | 'signup' | 'checkout'

type Props = {
  title?: string
  type: 'event' | 'course' | 'tool' | 'post' | 'funnel' | 'challenge'
  access: AccessResult
  mode?: 'restricted' | 'offer'
  step?: GateStep
  eventImage?: string
  onSuccess?: () => void | Promise<void>
}

type AccessCatalogLevel = {
  id: number
  name: string
}

type AccessCatalogBadge = {
  id: number
  name: string
  description?: string
}

type AccessCatalogApi = {
  ok?: boolean
  levels?: AccessCatalogLevel[]
  badges?: AccessCatalogBadge[]
}

const FREQUENCY_ORDER: Record<string, number> = {
  day: 0,
  week: 1,
  semi_month: 2,
  month: 3,
  year: 4,
}

const formatPrice = (value: number | string) =>
  new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0)

export function AccessDeniedModal({
  title,
  type,
  access,
  mode = 'restricted',
  eventImage,
  step: initialStep,
  onSuccess,
}: Props) {
  const { loggedIn, userId, refreshUser } = useCurrentUser()
  const [step, setStep] = useState<GateStep>(initialStep ?? 'preview')
  const [products, setProducts] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [selectedProductId, setSelectedProductId] = useState<number | null>(null)
  const [selectedRecurringId, setSelectedRecurringId] = useState<number | null>(null)

  const [catalogLevels, setCatalogLevels] = useState<AccessCatalogLevel[]>([])
  const [catalogBadges, setCatalogBadges] = useState<AccessCatalogBadge[]>([])
  
  // ─────────── FETCH PRODUCT(S) ───────────
  useEffect(() => {
    let cancelled = false

    if (access.reason !== 'product' || !access.productIds?.length) {
      setProducts([])
      setSelectedProductId(null)
      setLoading(false)
      return
    }

    setLoading(true)
    setProducts([])
    setSelectedProductId(null)

    Promise.all(
      access.productIds.map(async (pid) => {
        try {
          const res = await fetch(`/api/product/${pid}`, {
            cache: 'no-store',
          })

          if (!res.ok) return null

          const product = await res.json()

          if (!product || Number(product.product_id) !== Number(pid)) {
            return null
          }

          // Para contenidos comprables desde la UI,
          // solo ofrecemos productos públicos.
          // Esto NO modifica los permisos de acceso.
          if (
            (type === 'course' || type === 'challenge') &&
            Number(product.can_view) !== 1
          ) {
            return null
          }

          return product
        } catch {
          return null
        }
      })
    )
      .then((data) => {
        if (cancelled) return
        setProducts(data.filter((p) => p !== null))
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [access.reason, access.productIds, type])


  useEffect(() => {
    let alive = true

    ;(async () => {
      try {
        const res = await fetch('/api/access/catalog/public', {
          method: 'GET',
          cache: 'no-store',
        })

        const json = (await res.json().catch(() => null)) as AccessCatalogApi | null
        if (!alive) return

        if (res.ok && json?.ok) {
          setCatalogLevels(Array.isArray(json.levels) ? json.levels : [])
          setCatalogBadges(Array.isArray(json.badges) ? json.badges : [])
        }
      } catch {
        // silencioso
      }
    })()

    return () => {
      alive = false
    }
  }, [])

  const selectedProduct = useMemo(
    () => products.find((p) => p.product_id === selectedProductId) || null,
    [products, selectedProductId]
  )

  const sortedRecurrings = useMemo(() => {
    const recurrings = selectedProduct?.recurrings ?? []

    return [...recurrings].sort((a: any, b: any) => {
      const fa = FREQUENCY_ORDER[a.frequency] ?? 99
      const fb = FREQUENCY_ORDER[b.frequency] ?? 99

      if (fa !== fb) return fa - fb
      return Number(a.cycle ?? 1) - Number(b.cycle ?? 1)
    })
  }, [selectedProduct])

  const requiredLevels = useMemo(() => {
    const ids = new Set(access.levelIds || [])
    return catalogLevels.filter((level) => ids.has(level.id))
  }, [catalogLevels, access.levelIds])

  const requiredBadges = useMemo(() => {
    const ids = new Set(access.badgeIds || [])
    return catalogBadges.filter((badge) => ids.has(badge.id))
  }, [catalogBadges, access.badgeIds])

  const activeRecurring = useMemo(() => {
    return (
      sortedRecurrings.find(
        (r: any) => r.recurring_id === selectedRecurringId
      ) || null
    )
  }, [sortedRecurrings, selectedRecurringId])

  const special = Number(selectedProduct?.special || 0)
  const regularPrice = Number(selectedProduct?.price || 0)
  const recurringPrice = Number(activeRecurring?.price || 0)

  const hasSpecial = special > 0 && special < regularPrice
  const hasRecurring = recurringPrice > 0

  const price = hasRecurring
    ? recurringPrice
    : hasSpecial
      ? special
      : regularPrice

  const regularPriceUSD = Number(selectedProduct?.priceUSD || regularPrice || 0)
  const specialUSD = Number(selectedProduct?.specialUSD || special || 0)
  const recurringPriceUSD = Number(activeRecurring?.price || 0)

  const priceUSD = hasRecurring
    ? recurringPrice
    : hasSpecial
      ? specialUSD
      : regularPriceUSD

  const handleUnlock = async () => {
    if (!selectedProduct) return
    if (!loggedIn) {
      setStep('signup')
    } else {
      setStep('checkout')
    }
  }

  useEffect(() => {
    if (!sortedRecurrings.length) {
      setSelectedRecurringId(null)
      return
    }

    if (sortedRecurrings.length === 1) {
      setSelectedRecurringId(sortedRecurrings[0].recurring_id)
      return
    }

    setSelectedRecurringId((current) => {
      if (
        current !== null &&
        sortedRecurrings.some((r: any) => r.recurring_id === current)
      ) {
        return current
      }

      return sortedRecurrings[0].recurring_id
    })
  }, [sortedRecurrings])

  if (access.allowed) return null
  if (loading) return <p>Cargando producto…</p>

  const noun =
    type === 'event' ? 'este evento' :
    type === 'course' ? 'este curso' :
    type === 'tool' ? 'esta herramienta' :
    type === 'funnel' ? 'los embudos' :
    type === 'challenge' ? 'este checkpoint' :
    'este contenido'

  return (
    <div className="w-full max-w-md mx-auto p-6">
      <AnimatePresence mode="wait">

        {/* ─────────── PREVIEW ─────────── */}
        {step === 'preview' && (
          <motion.div
            key="preview"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="space-y-6 text-center"
          >
            {/* EVENTO HERO (opcional) */}
            {eventImage && (
              <div className="w-full h-48 relative overflow-hidden rounded-md">
                <img
                  src={eventImage}
                  alt={title}
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 flex items-center justify-center">
                  <Lock className="w-12 h-12 text-white opacity-80" />
                </div>
              </div>
            )}

            {/* TÍTULO */}
            <h2 className="text-2xl font-bold">{title}</h2>

            {/* INSTRUCCIÓN */}
            {access.reason === 'product' && products.length > 0 && (
              <p className="text-sm text-muted-foreground">
                Desbloquea {noun} adquiriendo uno de los siguientes productos:
              </p>
            )}

            {access.reason === 'product' && products.length === 0 && (
              <p className="text-sm text-muted-foreground">
                Este curso requiere una autorización de acceso.
                Contacta al administrador para obtener más información.
              </p>
            )}

            {/* LISTA DE PRODUCTOS */}
            {access.reason === 'product' && products.length > 0 && (
              <div className="space-y-3 mt-4 text-left">
                {products.map((p) => {
                  const hasSpecial =
                    p.special &&
                    !isNaN(Number(p.special)) &&
                    Number(p.special) > 0 &&
                    Number(p.special) < Number(p.price)

                  return (
                    <label
                      key={p.product_id}
                      className="flex justify-between items-center border rounded-md px-3 py-2 cursor-pointer hover:bg-muted/20"
                    >
                      <div className="flex items-center gap-3">
                        <input
                          type="radio"
                          name="unlockProduct"
                          value={p.product_id}
                          checked={selectedProductId === p.product_id}
                          onChange={() => setSelectedProductId(p.product_id)}
                          className="h-4 w-4 accent-primary"
                        />
                        <span>{p.name}</span>
                      </div>

                      <span className="font-semibold">
                        {hasSpecial ? (
                          <>
                            <span className="line-through text-sm text-muted-foreground mr-2">
                              ${formatPrice(p.price)}
                            </span>
                            <span className="text-lg font-bold text-green-600">
                              ${formatPrice(p.special)}
                            </span>{" "}
                            {p.currency || "USD"}
                          </>
                        ) : (
                          <>
                            ${formatPrice(p.price)} {p.currency || "USD"}
                          </>
                        )}
                      </span>
                    </label>
                  )
                })}
              </div>
            )}

            {sortedRecurrings.length > 1 && (
              <div className="space-y-2">
                <Label>Elige tu plan</Label>
                <div className="grid gap-2">
                  {sortedRecurrings.map((plan: any) => {
                    const selected = selectedRecurringId === plan.recurring_id

                    return (
                      <button
                        key={plan.recurring_id}
                        type="button"
                        onClick={() => setSelectedRecurringId(plan.recurring_id)}
                        className={[
                          'w-full rounded-xl border p-3 text-left transition',
                          selected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/40'
                        ].join(' ')}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div>
                            <div className="font-medium">{plan.name}</div>
                            <div className="text-sm text-muted-foreground">
                              {plan.cycle === 1
                                ? `Cada ${plan.frequency}`
                                : `Cada ${plan.cycle} ${plan.frequency}${plan.cycle > 1 ? 's' : ''}`}
                            </div>
                          </div>

                          <div className="text-right font-semibold">
                            ${formatPrice(plan.price || 0)} {selectedProduct?.currency || 'USD'}
                          </div>
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* BOTÓN DESBLOQUEAR */}
            {access.reason === 'product' && products.length > 0 && (
              <Button
                className="w-full mt-4 text-lg py-4"
                onClick={handleUnlock}
                disabled={!selectedProductId}
              >
                <ShoppingCart className="w-5 h-5 mr-2" />
                DESBLOQUEAR
                {selectedProduct && ` POR $${formatPrice(price)}`}
              </Button>
            )}

            {sortedRecurrings.length === 1 && activeRecurring && (
              <div className="text-sm text-muted-foreground">
                {activeRecurring.name}: ${formatPrice(activeRecurring.price || 0)} {selectedProduct?.currency || 'USD'}
              </div>
            )}

            {/* NIVEL */}
            {access.reason === 'level' && access.levelIds?.length && (
              <>
                <div className="flex justify-center">
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                    <Lock className="w-7 h-7 opacity-60" />
                  </div>
                </div>

                <div className="space-y-1">
                  <h3 className="font-semibold text-lg">Acceso restringido</h3>
                  <p className="text-sm opacity-70">Aún no tienes acceso a {noun}.</p>
                </div>

                <div className="space-y-4">
                  <p className="text-sm">Este contenido requiere alcanzar un nivel superior.</p>

                  <div className="flex justify-center gap-2 flex-wrap">
                    {requiredLevels.map((level) => (
                      <span
                        key={level.id}
                        className="px-3 py-1 text-xs rounded-full bg-muted"
                      >
                        {level.name}
                      </span>
                    ))}
                  </div>

                  <Button className="w-full">
                    <Star className="w-4 h-4 mr-1" />
                    Subir de nivel
                  </Button>
                </div>
              </>
            )}

            {/* BADGE */}
            {access.reason === 'badge' && access.badgeIds?.length && (
              <>
                <div className="flex justify-center">
                  <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
                    <Lock className="w-7 h-7 opacity-60" />
                  </div>
                </div>

                <div className="space-y-1">
                  <h3 className="font-semibold text-lg">Acceso restringido</h3>
                  <p className="text-sm opacity-70">Aún no tienes acceso a {noun}.</p>
                </div>

                <div className="space-y-4">
                  <p className="text-sm">Este contenido requiere una insignia específica.</p>

                  <div className="flex flex-wrap justify-center gap-3">
                    {requiredBadges.map((badge) => (
                      <div
                        key={badge.id}
                        className="flex items-center gap-2 rounded-full bg-muted px-3 py-2 text-xs"
                      >
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-background">
                          <Award className="h-3.5 w-3.5 opacity-70" />
                        </div>

                        <span className="max-w-[160px] truncate">{badge.name}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}

          </motion.div>
        )}

        {/* ─────────── SIGNUP ─────────── */}
        {step === 'signup' && selectedProduct && (
          <motion.div
            key="signup"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -40 }}
          >
            {/* Aquí tu componente de signup, ejemplo: */}
            <p>Formulario de registro aquí</p>
          </motion.div>
        )}

        {/* ─────────── CHECKOUT ─────────── */}
        {step === 'checkout' && selectedProduct && (
          <motion.div
            key="checkout"
            initial={{ opacity: 0, x: 40 }}
            animate={{ opacity: 1, x: 0 }}
          >            
            <CheckoutFormBlock
              data={{
                ...buildPurchaseContext({
                  product: selectedProduct,
                  activeRecurring,
                  selectedRecurringId,
                  price,
                  priceUSD,
                  paymentMethods: selectedProduct.paymentMethods || [],
                }),
              }}
              isEditing={false}
              onSuccess={async () => {
                await refreshUser?.()
                await onSuccess?.()
              }}
            />
          </motion.div>
        )}

      </AnimatePresence>
    </div>
  )
}
